// src/components/Navbar.js
import React, { useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Logo from '../components/Logo';
import { useAuth } from '../context/AuthContext';
import { canAccessDocumentDigitizationPage } from '../config/documentFeatures';
import { isContentBlockerDetected } from '../utils/contentBlockerSignals';

const MySwal = withReactContent(Swal);

function Navbar() {
  const { user, commerceName, branchName, commerceLogo, logout } = useAuth();
  const [dni, setDni] = useState('');
  const navigate = useNavigate();

  const userInitials = [user?.firstname, user?.lastname]
    .filter(Boolean)
    .map((s) => (s.charAt(0) || '').toUpperCase())
    .join('')
    .slice(0, 2) || '?';

  const handleDniChange = (e) => {
    const value = e.target.value;
    // Solo permitir números y limitar a 8 caracteres
    if (value === '' || (/^\d+$/.test(value) && value.length <= 8)) {
      setDni(value);
    }
  };

  const redirectBasedOnDni = async () => {
    if (isContentBlockerDetected()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Detectamos un bloqueador',
        text: 'Desactivalo para este sitio antes de iniciar la venta. En el icono del bloqueador, pausalo en este dominio y recargá.',
      });
      return;
    }

    if (dni.length < 7 || dni.length > 8) {
      MySwal.fire({
        icon: 'warning',
        title: 'DNI inválido',
        text: 'El DNI debe tener entre 7 y 8 caracteres.',
      });
      return;
    }

    navigate(`/clientes/${dni}`);
    setDni(''); // Limpiamos el input después de navegar
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      redirectBasedOnDni();
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  return (
    <header className="mb-4 rg-header">
      <div className="header-top">
        <div className="container d-flex align-items-center rg-header__inner">
          <div className="logo me-3">
            <Logo />
          </div>

          <div className="col-md-4 header-center">
            <div className="search-input input-group rg-header-search">
              <span className="input-group-text">
                <i className="bi bi-person-vcard"></i>
              </span>
              <input
                type="text"
                className="form-control form-control-lg"
                placeholder="Ingrese DNI del cliente..."
                value={dni}
                onChange={handleDniChange}
                onKeyDown={handleKeyDown}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <button
                type="button"
                className="btn icon btn-light rg-header-search__btn"
                aria-label="Buscar cliente"
                onClick={redirectBasedOnDni}
              >
                <FontAwesomeIcon icon={faMagnifyingGlass} size="2x" />
              </button>
            </div>
          </div>

          <div className="header-top-right rg-header-user">
            <div className="dropdown">
              <a href="#" id="topbarUserDropdown" className="user-dropdown rg-user-toggle d-flex align-items-center dropdown-toggle" data-bs-toggle="dropdown" data-bs-display="static" aria-expanded="false" >
                <div className="ms-2 avatar avatar-md2 border avatar-initials" title={`${user.firstname} ${user.lastname}`}>
                  <span>{userInitials}</span>
                </div>
                <div className="text">
                  <h6 className="user-dropdown-name">{user.firstname} {user.lastname}</h6>
                  <p className="user-dropdown-status text-sm text-muted">
                    {commerceName} ({branchName})
                  </p>
                </div>
              </a>
              <ul className="dropdown-menu dropdown-menu-end shadow-lg topbar-user-menu">
                <li>
                  <NavLink to="/" className="dropdown-item" end>
                    <i className="bi bi-house me-2"></i> Inicio
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/ventas" className="dropdown-item">
                    <i className="bi bi-cart me-2"></i> Ventas
                  </NavLink>
                </li>
                <li>
                  {process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' && (
                    <NavLink to="/cobros" className="dropdown-item">
                      <i className="bi bi-cash me-2"></i> Cobros
                    </NavLink>
                  )}
                </li>
                {process.env.REACT_APP_ENABLE_OBSERVATIONS_MANAGEMENT === 'true' && (
                <li>
                  <NavLink to="/observaciones" className="dropdown-item">
                    <i className="bi bi-exclamation-circle me-2"></i> Observaciones
                  </NavLink>
                </li>
                )}
                <li>
                  <hr className="dropdown-divider" />
                </li>
                {/* <li>
                  <a className="dropdown-item" href="#">
                    <i className="bi bi-gear me-2"></i> Configuración
                  </a>
                </li> */}
                {canAccessDocumentDigitizationPage() && (
                <li>
                  <NavLink to="/documentos" className="dropdown-item">
                    <i className="bi bi-upload me-2"></i> Digitalizar documentos
                  </NavLink>
                </li>
                )}
                {process.env.REACT_APP_ENABLE_ZREPORT_MANAGEMENT === 'true' && (
                  <li>
                    <NavLink to="/reportes-z" className="dropdown-item">
                      <i className="bi bi-receipt me-2"></i> Reportes Z
                    </NavLink>
                  </li>
                )}
                <li>
                  <hr className="dropdown-divider" />
                </li>
                <li>
                  <button
                    className="dropdown-item text-end"
                    onClick={handleLogout}
                  >
                    Cerrar sesión <i className="bi bi-box-arrow-right me-2"></i>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
